// Value score and calculation utilities

/**
 * Calculate the value score for a session
 * Formula: (rating × duration) / price
 * 
 * @param rating Overall rating (1-10)
 * @param duration Duration in minutes
 * @param price Price paid in dollars
 * @returns Value score (higher is better)
 */
export function calculateValueScore(
    rating: number,
    duration: number,
    price: number
): number {
    if (!price || price === 0 || !duration || !rating) {
        return 0;
    }

    return Number(((rating * duration) / price).toFixed(2));
}

/**
 * Calculate average rating for a set of sessions
 */
export function calculateAverageRating(ratings: number[]): number {
    if (ratings.length === 0) return 0;

    const sum = ratings.reduce((acc, rating) => acc + rating, 0);
    return Number((sum / ratings.length).toFixed(1));
}

/**
 * Calculate average effects from sessions
 */
export function calculateAverageEffects(
    sessions: Array<{
        anxiety?: number;
        body_high?: number;
        head_high?: number;
        creativity?: number;
        smoothness?: number;
    }>
): {
    anxiety: number;
    body_high: number;
    head_high: number;
    creativity: number;
    smoothness: number;
} {
    if (sessions.length === 0) {
        return {
            anxiety: 0,
            body_high: 0,
            head_high: 0,
            creativity: 0,
            smoothness: 0,
        };
    }

    const totals = sessions.reduce<{
        anxiety: number;
        body_high: number;
        head_high: number;
        creativity: number;
        smoothness: number;
    }>(
        (acc, session) => ({
            anxiety: acc.anxiety + (session.anxiety ?? 0),
            body_high: acc.body_high + (session.body_high ?? 0),
            head_high: acc.head_high + (session.head_high ?? 0),
            creativity: acc.creativity + (session.creativity ?? 0),
            smoothness: acc.smoothness + (session.smoothness ?? 0),
        }),
        { anxiety: 0, body_high: 0, head_high: 0, creativity: 0, smoothness: 0 }
    );

    return {
        anxiety: Number((totals.anxiety / sessions.length).toFixed(1)),
        body_high: Number((totals.body_high / sessions.length).toFixed(1)),
        head_high: Number((totals.head_high / sessions.length).toFixed(1)),
        creativity: Number((totals.creativity / sessions.length).toFixed(1)),
        smoothness: Number((totals.smoothness / sessions.length).toFixed(1)),
    };
}

/**
 * Get top N sessions by value score
 */
export function getTopValueSessions<T extends { value_score?: number }>(
    sessions: T[],
    count: number = 3
): T[] {
    return [...sessions]
        .filter(s => s.value_score && s.value_score > 0)
        .sort((a, b) => (b.value_score || 0) - (a.value_score || 0))
        .slice(0, count);
}

/**
 * Get bottom N sessions by value score
 */
export function getLowestValueSessions<T extends { value_score?: number }>(
    sessions: T[],
    count: number = 3
): T[] {
    return [...sessions]
        .filter(s => s.value_score && s.value_score > 0)
        .sort((a, b) => (a.value_score || 0) - (b.value_score || 0))
        .slice(0, count);
}

/**
 * Calculate total spent from sessions
 */
export function calculateTotalSpent(sessions: Array<{ price_paid?: number, session_cost?: number }>): number {
    return sessions.reduce((total, session) => total + (session.session_cost || session.price_paid || 0), 0);
}
